import { useEffect, useRef, useState } from "react";
import { Box, Button, ButtonGroup, Dialog, useToast } from "@hightouch/htui";
import { DYNAMIC_SLOT_ATTRIBUTE } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/shared";
import { MarkdownStaticHtml } from "src/components/markdown-editor";
import { usePrintToPdf } from "src/components/llm/utils/use-print-to-pdf";
import type { AmpLibraryFileFragment } from "src/graphql";
import { WebPageDownloadDialog } from "src/pages/ad-studio/files/web-page-download-dialog";
import { useAdAssetDownload } from "src/pages/ad-studio/hooks/use-ad-asset-download";
import { DownloadCompiledHtmlModal } from "src/pages/lifecycle-studio/emails/components/download-compiled-html-modal";
import { ExportEmailPngModal } from "src/pages/lifecycle-studio/emails/components/export-email-png-modal";
import { materializeSlotSelections } from "src/pages/lifecycle-studio/emails/email-editor/canvas/dynamic-content/materialize-slot-selections";
import { useSavedDynamicSlots } from "src/pages/lifecycle-studio/emails/email-editor/canvas/dynamic-content/use-saved-dynamic-slots";
import { useEmailPreviewRender } from "src/pages/lifecycle-studio/emails/hooks/use-email-preview-render";
import type { EmailExportFormat } from "src/pages/lifecycle-studio/emails/png-export/export-email";
import { renderDocHtml } from "src/pages/projects/library/files/doc-html";
import {
  DOC_FORMAT_OPTIONS,
  type DocFormat,
  EMAIL_FORMAT_OPTIONS,
  type EmailFormat,
  isEmailExportFormat,
} from "src/pages/projects/library/files/export-formats";
import {
  downloadFile,
  getFileDownload,
} from "src/pages/projects/library/files/file-download";
import { printEmailHtmlAsPdfAsync } from "src/pages/lifecycle-studio/utils/print-email-as-pdf";
import { FormatCheckboxes } from "src/pages/projects/library/files/format-checkboxes";
import { downloadText, sanitizeFilename } from "src/utils/download";

/** A second dialog that finishes part of the download, shown one after another. */
type EmailHandoff =
  | { kind: "compiled-html" }
  | { kind: "rendered"; formats: EmailExportFormat[] };

/** The export options each file type's own editor offers, so the library and canvas download it the same way. */
export function DownloadFileDialog({
  file,
  onClose,
}: {
  file: AmpLibraryFileFragment;
  onClose: () => void;
}) {
  const name = sanitizeFilename(file.name ?? "") || "Untitled";
  if (file.ad)
    return (
      <AdDownload assetId={file.ad.assetId} name={name} onClose={onClose} />
    );
  if (file.email?.html)
    return (
      <EmailDownloadDialog
        emailId={file.email.emailId}
        versionId={file.versionId ?? null}
        html={file.email.html}
        subject={file.email.subject ?? undefined}
        preheader={file.email.preheader ?? undefined}
        name={name}
        onClose={onClose}
      />
    );
  if (file.html)
    return (
      <WebPageDownloadDialog
        fileId={file.id}
        name={name}
        content={file.html}
        onClose={onClose}
      />
    );
  if (file.markdown)
    return (
      <DocDownloadDialog
        file={file}
        markdown={file.markdown.markdown}
        name={name}
        onClose={onClose}
      />
    );
  return null;
}

function AdDownload({
  assetId,
  name,
  onClose,
}: {
  assetId: string;
  name: string;
  onClose: () => void;
}) {
  const { downloadAssets, downloadDialog } = useAdAssetDownload({
    videoFilename: name,
  });
  const requested = useRef(false);
  const [isRequestSettled, setIsRequestSettled] = useState(false);
  useEffect(() => {
    // Development runs effects twice, and one click must open one export.
    if (requested.current) return;
    requested.current = true;
    void downloadAssets([assetId]).finally(() => setIsRequestSettled(true));
  }, [assetId, downloadAssets]);
  useEffect(() => {
    if (isRequestSettled && !downloadDialog) onClose();
  }, [isRequestSettled, downloadDialog, onClose]);
  return <>{downloadDialog}</>;
}

function EmailDownloadDialog({
  emailId,
  versionId,
  html,
  subject,
  preheader,
  name,
  onClose,
}: {
  emailId: string;
  versionId: string | null;
  html: string;
  subject?: string;
  preheader?: string;
  name: string;
  onClose: () => void;
}) {
  const [formats, setFormats] = useState<Set<EmailFormat>>(
    () => new Set(["html"]),
  );
  const [handoffs, setHandoffs] = useState<EmailHandoff[]>([]);
  // The compiled HTML modal calls onClose after a download and on cancel alike, so its onDownloaded marks which one happened.
  const isHandoffFinished = useRef(false);
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);
  const { toast } = useToast();
  const { slots } = useSavedDynamicSlots(emailId);
  const { fetchRenderedHtml } = useEmailPreviewRender({
    contentEmailId: emailId,
    versionId,
    enabled: false,
  });

  /** Resolves to the PDF's HTML, or undefined after reporting a failure. */
  const preparePdf = async () => {
    setIsPreparingPdf(true);
    try {
      // Content blocks expand only in the server render, and a PDF proof has to show them.
      const renderedHtml = await fetchRenderedHtml();
      return materializeSlotSelections(renderedHtml || html, slots, {});
    } catch (error) {
      toast({
        id: "file-download",
        title: "Couldn't prepare the PDF",
        message: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
      return undefined;
    } finally {
      setIsPreparingPdf(false);
    }
  };

  const confirm = async () => {
    const pdfHtml = formats.has("pdf") ? await preparePdf() : undefined;
    if (formats.has("pdf") && pdfHtml === undefined) return;
    // Slot markers mean nothing outside Hightouch, so dynamic content compiles to the provider's dialect.
    const isDynamic = html.includes(DYNAMIC_SLOT_ATTRIBUTE);
    if (formats.has("html") && !isDynamic) downloadText(html, `${name}.html`);
    const rendered = [...formats].filter(isEmailExportFormat);
    const queue: EmailHandoff[] = [];
    if (formats.has("html") && isDynamic) queue.push({ kind: "compiled-html" });
    if (rendered.length > 0)
      queue.push({ kind: "rendered", formats: rendered });
    if (queue.length > 0) setHandoffs(queue);
    else onClose();
    if (pdfHtml !== undefined)
      await printEmailHtmlAsPdfAsync({
        html: pdfHtml,
        title: name,
        subject,
        preheader,
      });
  };

  const [handoff, ...remaining] = handoffs;
  // Finishing a step opens the next one, while cancelling any step ends the whole download.
  const finishHandoff = () => {
    isHandoffFinished.current = false;
    if (remaining.length > 0) setHandoffs(remaining);
    else onClose();
  };

  if (handoff?.kind === "compiled-html")
    return (
      <DownloadCompiledHtmlModal
        isOpen
        onDownloaded={() => {
          isHandoffFinished.current = true;
        }}
        onClose={() =>
          isHandoffFinished.current ? finishHandoff() : onClose()
        }
        emailId={emailId}
        versionId={versionId}
        emailName={name}
      />
    );
  if (handoff?.kind === "rendered" && versionId)
    return (
      <ExportEmailPngModal
        isOpen
        formats={handoff.formats}
        onClose={finishHandoff}
        contentEmailId={emailId}
        contentEmailVersionId={versionId}
        filename={name}
      />
    );

  return (
    <Dialog
      isOpen
      variant="form"
      width="md"
      title="Download email"
      onClose={onClose}
      actions={
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={formats.size === 0}
            isLoading={isPreparingPdf}
            onClick={() => void confirm()}
          >
            Continue
          </Button>
        </ButtonGroup>
      }
    >
      <FormatCheckboxes
        options={EMAIL_FORMAT_OPTIONS.map((option) => ({
          ...option,
          isDisabled: !versionId && isEmailExportFormat(option.value),
        }))}
        value={formats}
        onChange={setFormats}
      />
    </Dialog>
  );
}

function DocDownloadDialog({
  file,
  markdown,
  name,
  onClose,
}: {
  file: AmpLibraryFileFragment;
  markdown: string;
  name: string;
  onClose: () => void;
}) {
  const [formats, setFormats] = useState<Set<DocFormat>>(() => new Set(["md"]));
  const [isPrinting, setIsPrinting] = useState(false);
  const { toast } = useToast();
  // The dialog stays open until printing ends, since the print copies the mounted document.
  const { contentRef, handlePrint } = usePrintToPdf(name, {
    hideBrowserHeaders: true,
    onAfterPrint: onClose,
    onPrintError: (error) => {
      setIsPrinting(false);
      toast({
        id: "file-download",
        title: "Couldn't prepare the PDF",
        message: error.message,
        variant: "error",
      });
    },
  });

  const confirm = () => {
    const download = getFileDownload(file);
    if (formats.has("md") && download) downloadFile(download);
    if (!formats.has("pdf")) {
      onClose();
      return;
    }
    setIsPrinting(true);
    handlePrint();
  };

  return (
    <Dialog
      isOpen
      variant="form"
      width="md"
      title="Download document"
      onClose={onClose}
      actions={
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={formats.size === 0}
            isLoading={isPrinting}
            onClick={confirm}
          >
            Download document
          </Button>
        </ButtonGroup>
      }
    >
      <FormatCheckboxes
        options={DOC_FORMAT_OPTIONS}
        value={formats}
        onChange={setFormats}
      />
      {/* The print copies only the ref'd node, so this hidden wrapper keeps the document off screen without hiding it in the PDF. */}
      <Box display="none">
        <Box ref={contentRef}>
          <MarkdownStaticHtml html={renderDocHtml(markdown)} />
        </Box>
      </Box>
    </Dialog>
  );
}
